interface EnvConfig {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_SENTRY_DSN?: string;
  VITE_GA_MEASUREMENT_ID?: string;
  VITE_GPT_PROXY_URL?: string;
  VITE_RSS_PROXY_URL?: string;
  VITE_MONITOR_PROXY_URL?: string;
  VITE_MONITOR_STREAM_URL?: string;
}

class EnvironmentValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvironmentValidationError';
  }
}

// Throws in production if required vars are missing; warns in development.
export function validateEnvironment(): EnvConfig {
  const requiredVars = [
    'VITE_SUPABASE_URL',
    'VITE_SUPABASE_ANON_KEY',
  ] as const;

  const missing: string[] = [];

  for (const varName of requiredVars) {
    const value = import.meta.env[varName];
    if (!value || value === 'placeholder' || value.includes('placeholder')) {
      missing.push(varName);
    }
  }

  if (missing.length > 0) {
    const errorMessage =
      `Missing required environment variables:\n${missing.map(v => `  - ${v}`).join('\n')}\n\n` +
      'Please create a .env file with the required variables.\n' +
      'See .env.example for reference.';

    if (import.meta.env.PROD) {
      throw new EnvironmentValidationError(errorMessage);
    }

    console.warn('âš ï¸ Environment Configuration Warning:\n' + errorMessage);
  }

  return {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
    VITE_GA_MEASUREMENT_ID: import.meta.env.VITE_GA_MEASUREMENT_ID,
    VITE_GPT_PROXY_URL: import.meta.env.VITE_GPT_PROXY_URL,
    VITE_RSS_PROXY_URL: import.meta.env.VITE_RSS_PROXY_URL,
    VITE_MONITOR_PROXY_URL: import.meta.env.VITE_MONITOR_PROXY_URL,
    VITE_MONITOR_STREAM_URL: import.meta.env.VITE_MONITOR_STREAM_URL,
  };
}

export function getEnv(key: keyof EnvConfig, required = false): string | undefined {
  const value = import.meta.env[key];

  if (required && (!value || value === 'placeholder')) {
    throw new EnvironmentValidationError(`Required environment variable ${key} is not set`);
  }

  return value;
}

export function isProduction(): boolean {
  return import.meta.env.PROD;
}

export function isDevelopment(): boolean {
  return import.meta.env.DEV;
}


